/**
 * WHICH NUMBERING A REFERENCE IS IN, AND WHAT ANOTHER TRADITION CALLS THE SAME VERSE.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────
 * WHAT THIS IS NOT, BECAUSE THE LARGER VERSION WAS ASKED FOR AND ASSESSED AND DECLINED.
 *
 * The request was to make the library numbering-agnostic: pick an edition, and every other
 * edition realigns to ITS numbering. That is the right idea and it is not buildable on the data
 * this project has, which was established by measurement rather than argued:
 *
 *   THE ONE MAPPED PAIR IS LOSSY. Hebrew↔King James is the only scheme relation with real tables
 *   here, and it is modelled as a WITHIN-CHAPTER offset. Exodus 8 is `-4`, described as "Hebrew
 *   7:26-29 is the KJV 8:1-4" — and 7:26 is in the previous chapter, so `realign` returns nothing
 *   for KJV 8:1-4. Four verses in that chapter, 72 across the 35-chapter table. The model cannot
 *   express the thing versification differences are made of: verses crossing a chapter boundary.
 *
 *   THE OTHER SCHEMES HAVE NO MAPPING AT ALL. The qirāʾāt give per-surah COUNTS — Warsh has 105
 *   ayat in surah 18 against Ḥafṣ's 110 — and no statement of which Warsh ayah IS Ḥafṣ 18:100.
 *   The Vulgate and Septuagint Psalters need a merge (Psalms 9+10 → 9) and a split (147 →
 *   146+147), and an offset cannot represent a merge.
 *
 *   AND THE RISK LANDS WHERE THIS PROJECT HAS ALREADY BEEN BURNED. Agnostic numbering means
 *   numbering stops being identity, so every ref-keyed structure — search, parallels, contested
 *   passages, the graph, permalinks, the packed per-verse corpus string — becomes scheme-relative.
 *   The failure is not a crash but a right-looking citation over the wrong verse, which is the
 *   Douay-Rheims Psalter, the Metsudah cache key, and the eBible book codes, all over again.
 *
 * SO THIS RELABELS AND NEVER REALIGNS. Identity stays King James. No text moves, no key changes,
 * nothing downstream becomes scheme-relative. All this does is answer "what does a Tanakh call
 * this verse?" — which is the reader's actual question, and a display concern.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */

/**
 * The numbering conventions this library can name a verse in.
 *
 * `kjv` is the identity: every `ref` in every artifact is in it, and that does not change.
 * `hebrew` is the Masoretic numbering a Tanakh uses. `jps` is the JPS 1917's, which follows the
 * Hebrew but was measured separately against a different digitisation and is NOT assumed to agree
 * — two tables describing two texts, kept apart on purpose.
 */
export type NumberingScheme = 'kjv' | 'hebrew' | 'jps';

/** A chapter where a scheme numbers differently from the King James. */
export interface SchemeShift {
  /** `${book} ${chapter}` as a King James reference carries it. */
  chapter: string;
  /**
   * THE CONVENTION, and both source tables already share it: `schemeVerse = kjvVerse + offset`.
   *
   * Exodus 8 is -4, so the King James' 8:5 is the Hebrew's 8:1. The Psalms are +1, so the King
   * James' 22:1 is the Hebrew's 22:2 — the Hebrew counts the superscription.
   */
  offset: number;
  /** Why, so a row can be checked against a reference rather than trusted. */
  why: string;
}

const SUPERSCRIPTION = 'the Hebrew counts the superscription as verse 1';

/**
 * The Masoretic numbering, against the King James.
 *
 * THIS IS THE SOURCE OF TRUTH AND `pipeline/lib/versification.ts` READS IT FROM HERE. It used to
 * live in the pipeline, which made it build machinery; it is domain data — the same argument
 * `corpora.ts` opens with — and the browser needs it now that a reader is shown the other number.
 * One table, so a repair and a label can never disagree about what the Hebrew calls a verse.
 */
export const HEBREW_SHIFTS: SchemeShift[] = [
  { chapter: '1 Chronicles 6', offset: -15, why: 'Hebrew 5:27–41 is the KJV 6:1–15' },
  { chapter: 'Leviticus 6', offset: -7, why: 'Hebrew 5:20–26 is the KJV 6:1–7' },
  { chapter: 'Nehemiah 4', offset: -6, why: 'Hebrew 3:33–38 is the KJV 4:1–6' },
  { chapter: 'Deuteronomy 13', offset: 1, why: 'Hebrew 13:1 is the KJV 12:32' },
  { chapter: 'Exodus 8', offset: -4, why: 'Hebrew 7:26–29 is the KJV 8:1–4' },
  { chapter: 'Ezekiel 21', offset: 5, why: 'Hebrew 21:1–5 is the KJV 20:45–49' },
  { chapter: 'Daniel 4', offset: -3, why: 'Hebrew 3:31–33 is the KJV 4:1–3' },
  { chapter: 'Zechariah 2', offset: -4, why: 'Hebrew 1:18–21 is the KJV 2:1–4' },
  { chapter: 'Genesis 32', offset: 1, why: 'Hebrew 32:1 is the KJV 31:55' },
  { chapter: '1 Chronicles 12', offset: 1, why: 'Hebrew 12:1 is the KJV 11:47' },
  { chapter: '1 Samuel 21', offset: 1, why: 'Hebrew 21:1 is the KJV 20:42' },
  { chapter: '1 Samuel 24', offset: 1, why: 'Hebrew 24:1 is the KJV 23:29' },
  { chapter: '2 Samuel 19', offset: 1, why: 'Hebrew 19:1 is the KJV 18:33' },
  { chapter: '2 Kings 12', offset: 1, why: 'Hebrew 12:1 is the KJV 11:21' },
  { chapter: 'Nehemiah 10', offset: 1, why: 'Hebrew 10:1 is the KJV 9:38' },
  { chapter: 'Daniel 6', offset: 1, why: 'Hebrew 6:1 is the KJV 5:31' },
  { chapter: 'Hosea 12', offset: 1, why: 'Hebrew 12:1 is the KJV 11:12' },
  { chapter: 'Hosea 14', offset: 1, why: 'Hebrew 14:1 is the KJV 13:16' },
  { chapter: 'Nahum 2', offset: 1, why: 'Hebrew 2:1 is the KJV 1:15' },
  { chapter: 'Deuteronomy 29', offset: 1, why: 'Hebrew 29:1 is the KJV 28:69' },
  { chapter: '2 Chronicles 2', offset: 1, why: 'Hebrew 2:1 is the KJV 1:18' },
  { chapter: '2 Chronicles 14', offset: 1, why: 'Hebrew 14:1 is the KJV 13:23' },
  { chapter: 'Isaiah 9', offset: -1, why: 'Hebrew 8:23 is the KJV 9:1' },
  { chapter: 'Jeremiah 9', offset: -1, why: 'Hebrew 8:23 is the KJV 9:1' },
  { chapter: 'Micah 5', offset: -1, why: 'Hebrew 4:14 is the KJV 5:1' },
  /* The Psalms, all one cause. */
  ...['22', '48', '68', '69', '76', '80', '81', '83', '89', '102']
    .map(n => ({ chapter: `Psalms ${n}`, offset: 1, why: SUPERSCRIPTION })),
];

const BY_SCHEME: Record<NumberingScheme, Map<string, SchemeShift>> = {
  kjv: new Map(),
  hebrew: new Map(HEBREW_SHIFTS.map(s => [s.chapter, s])),
  /* The JPS is measured against its own digitisation in pipeline/lib/jps-versification.ts and is
     deliberately NOT aliased to the Hebrew table. Until it is moved here too, asking for a JPS
     label returns nothing rather than the Hebrew's answer dressed up as the JPS's. */
  jps: new Map(),
};

/** What a scheme calls a verse this library addresses as `book chapter:verse`. */
export interface SchemeLabel {
  chapter: number;
  verse: number;
  /** The row that produced it, so a reader-facing tooltip can say why rather than assert. */
  why: string;
}

/**
 * What `scheme` calls the King James verse `book chapter:verse`.
 *
 * `undefined` means one of three honest things, and they are deliberately not distinguished by the
 * return type because a caller must treat them identically — say nothing:
 *
 *   1. the scheme numbers this verse the same way, so there is nothing to add;
 *   2. the scheme has no table here yet (`jps`);
 *   3. THE COUNTERPART IS IN THE NEIGHBOURING CHAPTER and this model cannot name it. King James
 *      Exodus 8:1 is Hebrew 7:26, and knowing that requires the Hebrew length of chapter 7, which
 *      is not in this table. 72 verses across the table fall here. Returning a number computed
 *      as `1 + -4 = -3` would be worse than silence.
 */
export function labelIn(
  scheme: NumberingScheme,
  book: string,
  chapter: number,
  verse: number,
): SchemeLabel | undefined {
  const shift = BY_SCHEME[scheme].get(`${book} ${chapter}`);
  if (!shift) return undefined;
  const v = verse + shift.offset;
  /* Out of the chapter in either direction: its partner lives next door and cannot be named. */
  if (v < 1) return undefined;
  return { chapter, verse: v, why: shift.why };
}

/** Every chapter a scheme numbers differently — for disclosure text that must not go stale. */
export function shiftedChapters(scheme: NumberingScheme): readonly SchemeShift[] {
  return [...BY_SCHEME[scheme].values()];
}

/**
 * Split a reference into its parts.
 *
 * Book names contain spaces and digits — "1 Samuel", "Song of Solomon", "2 Chronicles" — so the
 * chapter and verse are taken from the END and everything before is the book. Splitting on the
 * first space would file every numbered book under "1", and it would do so silently, which is the
 * failure mode this project keeps meeting.
 */
export function parseRef(ref: string): { book: string; chapter: number; verse: number } | undefined {
  const m = /^(.+) (\d+):(\d+)$/.exec(ref.trim());
  if (!m) return undefined;
  return { book: m[1], chapter: Number(m[2]), verse: Number(m[3]) };
}

/**
 * What `scheme` calls the verse this library addresses as `ref`, ready to print.
 *
 * Returns the CHAPTER AND VERSE only, never the book, because the book name is already on the
 * page and the schemes here do not disagree about it. `undefined` wherever `labelIn` is
 * undefined — same reference, no table, or a counterpart in the next chapter.
 */
export function refLabelIn(scheme: NumberingScheme, ref: string): string | undefined {
  const parts = parseRef(ref);
  if (!parts) return undefined;
  const label = labelIn(scheme, parts.book, parts.chapter, parts.verse);
  if (!label) return undefined;
  /* An offset that lands on the same number is not worth showing — it would tell a reader that
     two conventions differ here when they agree. */
  if (label.chapter === parts.chapter && label.verse === parts.verse) return undefined;
  return `${label.chapter}:${label.verse}`;
}
